import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  EvalExtractionCoverage,
  EvalExtractionExpectedState,
  EvalExtractionOrigin,
  EvalExtractionType,
  ExtractionExpectedOutput,
} from '@artem-lex/common-server/evals/standardExtraction'
import { apiClient } from '@/shared/api/apiClient'
import { EVAL_TASK_DIRECTORY_QUERY_KEY, type EvalTaskStatus } from './evals'

export const EVAL_EXTRACTION_TASKS_QUERY_KEY = 'evalExtractionTasks'
export const EVAL_USER_STANDARDS_QUERY_KEY = 'evalUserStandards'

export interface EvalExtractionTaskVersion {
  id: string
  taskKey: string
  version: number
  status: EvalTaskStatus
  suiteKey: string
  extractionType: EvalExtractionType
  expectedState: EvalExtractionExpectedState
  coverage: EvalExtractionCoverage
  reviewedScope: string
  absenceBasis: string | null
  organizationId: string
  userStandardId: string
  standardSnapshot: {
    reference: string
    title: string
    type: string
    fileName: string | null
    sourceChecksum: string
  }
  extractionSnapshot: unknown
  extractionSnapshotHash: string
  expectedOutput: ExtractionExpectedOutput
  origin: EvalExtractionOrigin
  reviewNotes: string | null
  createdAt: string
  updatedAt: string
  publishedAt: string | null
}

export interface EvalUserStandardOption {
  id: string
  reference: string
  title: string
  type: string
  fileName: string | null
}

export function useEvalExtractionTasksList(
  status?: EvalTaskStatus,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY, status ?? 'ALL'],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await apiClient.get<{
        items: EvalExtractionTaskVersion[]
      }>('/admin/evals/extraction-tasks', {
        params: status != null ? { status } : undefined,
      })
      return response.data.items
    },
  })
}

export function useEvalExtractionTask(taskVersionId: string | undefined) {
  return useQuery({
    queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY, taskVersionId],
    enabled: taskVersionId != null,
    queryFn: async () => {
      const response = await apiClient.get<EvalExtractionTaskVersion>(
        `/admin/evals/extraction-tasks/${taskVersionId}`
      )
      return response.data
    },
  })
}

export function useEvalUserStandardsSearch(query: string) {
  return useQuery({
    queryKey: [EVAL_USER_STANDARDS_QUERY_KEY, query],
    queryFn: async () => {
      const response = await apiClient.get<{ items: EvalUserStandardOption[] }>(
        '/admin/evals/user-standards',
        { params: query.trim() ? { q: query } : undefined }
      )
      return response.data.items
    },
  })
}

export function useCreateEvalExtractionTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      userStandardId: string
      extractionType: EvalExtractionType
      origin?: EvalExtractionOrigin
    }) => {
      const response = await apiClient.post<EvalExtractionTaskVersion>(
        '/admin/evals/extraction-tasks',
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useUpdateEvalExtractionTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      expectedState?: EvalExtractionExpectedState
      coverage?: EvalExtractionCoverage
      reviewedScope?: string
      absenceBasis?: string | null
      expectedOutput?: ExtractionExpectedOutput
      reviewNotes?: string | null
    }) => {
      const { id, ...payload } = input
      const response = await apiClient.patch<EvalExtractionTaskVersion>(
        `/admin/evals/extraction-tasks/${id}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function usePublishEvalExtractionTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<EvalExtractionTaskVersion>(
        `/admin/evals/extraction-tasks/${id}/publish`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useReviseEvalExtractionTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<EvalExtractionTaskVersion>(
        `/admin/evals/extraction-tasks/${id}/revisions`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}

export function useDeleteEvalExtractionTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<{ id: string }>(
        `/admin/evals/extraction-tasks/${id}`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EVAL_EXTRACTION_TASKS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [EVAL_TASK_DIRECTORY_QUERY_KEY],
      })
    },
  })
}
